/**
 * useDarkMode — dark-mode toggle hook.
 *
 * Per locked decision D.4: dark mode is shipped from day one. The
 * semantic CSS vars in design-system/tokens/global.css are defined
 * for both `:root` (light) and `.dark` (dark). This hook is the
 * runtime that flips the `class="dark"` on `<html>`.
 *
 * Persistence: writes the user's choice to localStorage under
 * `smms-color-mode`. Survives reload. Listens to the OS-level
 * `prefers-color-scheme` change event when the user is on `auto`.
 *
 * Modes:
 *   - 'light' — force light (overrides OS pref)
 *   - 'dark'  — force dark (overrides OS pref)
 *   - 'auto'  — track OS preference (default for first-time users)
 *
 * Usage:
 *
 *   const { mode, resolved, setMode, toggle } = useDarkMode();
 *
 *   // Direct toggle button
 *   <Button onClick={toggle}>{resolved === 'dark' ? 'Light' : 'Dark'}</Button>
 *
 *   // Three-way picker
 *   <Select value={mode} onChange={e => setMode(e.target.value)}>
 *     <option value='auto'>System</option>
 *     <option value='light'>Light</option>
 *     <option value='dark'>Dark</option>
 *   </Select>
 *
 * Phase 11 wires this to a TopBar toggle in a follow-on commit. The
 * runtime itself ships now so primitive consumers can use `dark:`
 * Tailwind variants and read the resolved mode for chart libs.
 *
 * Spec: docs/design-system/01-tokens.md, 09-navigation-shell.md.
 */

import { useCallback, useEffect, useState } from 'react';

export type ColorMode = 'light' | 'dark' | 'auto';
export type ResolvedColorMode = 'light' | 'dark';

const STORAGE_KEY = 'smms-color-mode';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

function readStoredMode(): ColorMode {
  if (typeof window === 'undefined') return 'auto';
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'auto') return v;
  } catch {
    // ignore
  }
  return 'auto';
}

function readSystemDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(MEDIA_QUERY).matches;
}

function applyClass(resolved: ResolvedColorMode): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.classList.remove('dark');
    root.classList.add('light');
  }
}

export interface UseDarkModeResult {
  /** User's chosen mode — 'light', 'dark', or 'auto'. */
  mode: ColorMode;
  /** The mode actually applied right now ('light' or 'dark' — never 'auto'). */
  resolved: ResolvedColorMode;
  /** Set the mode. Persists to localStorage and applies immediately. */
  setMode: (mode: ColorMode) => void;
  /** Convenience: toggle between light and dark (skipping auto). */
  toggle: () => void;
}

export function useDarkMode(): UseDarkModeResult {
  const [mode, setModeState] = useState<ColorMode>(() => readStoredMode());
  const [systemDark, setSystemDark] = useState<boolean>(() => readSystemDark());

  // Track the OS preference so 'auto' mode reacts to changes.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    // Modern browsers
    if (mql.addEventListener) mql.addEventListener('change', handler);
    // Safari < 14
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  // Resolve the actual mode and apply.
  const resolved: ResolvedColorMode =
    mode === 'auto' ? (systemDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    applyClass(resolved);
  }, [resolved]);

  // Cross-tab sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setModeState(readStoredMode());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setMode = useCallback((next: ColorMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
    setModeState(next);
  }, []);

  const toggle = useCallback(() => {
    setMode(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setMode]);

  return { mode, resolved, setMode, toggle };
}

/**
 * Initialize the color-mode class on first paint, before React mounts.
 * Call this from main.tsx so the first frame doesn't flash the wrong mode.
 */
export function initColorModeFromStorage(): void {
  if (typeof document === 'undefined') return;
  const stored = readStoredMode();
  const systemDark = readSystemDark();
  const resolved: ResolvedColorMode =
    stored === 'auto' ? (systemDark ? 'dark' : 'light') : stored;
  applyClass(resolved);
}
